import { money } from "@/lib/format";
import { API } from "@/lib/api";

const day = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

const InfoCard = ({ title, children }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
      {title}
    </div>
    {children}
  </div>
);

export const InvoicePrintout = ({ invoice, template, logoUrl }) => {
  const t = template || {};
  const inv = invoice || {};
  const lines =
    (inv.lines || []).length > 0
      ? inv.lines
      : [{ description: inv.title, amount: inv.amount }];

  return (
    <div
      className="print-sheet mx-auto w-full max-w-[8.5in] bg-white p-5 text-slate-900 sm:p-10"
      data-testid="invoice-printout"
    >
      <header className="flex flex-wrap items-start justify-between gap-6">
        <div className="min-w-0">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={t.company_name || "Company"}
              data-testid="invoice-printout-logo"
              className="print-fixed print-fixed-logo mb-3 h-14 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}
          <div
            className="text-lg font-bold tracking-tight"
            data-testid="invoice-printout-company"
          >
            {t.company_name || "Your company"}
          </div>
          {t.address && (
            <div className="text-[13px] text-slate-500">{t.address}</div>
          )}
          {(t.phone || t.website) && (
            <div className="text-[13px] text-slate-500">
              {[t.phone, t.website].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 text-right">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
            Invoice
          </div>
          <div
            className="font-mono-data text-xl font-bold tracking-tight"
            data-testid="invoice-printout-number"
          >
            {inv.code ? `#${inv.code}` : "Invoice"}
          </div>
          <div className="mt-1 text-[12px] text-slate-500">
            Issued {day(inv.issued_at)}
          </div>
          <div className="mt-2 text-[12px] font-semibold text-slate-600">
            Due {inv.due_date ? day(inv.due_date) : "on install"}
          </div>
          <div
            className="font-mono-data mt-2 text-2xl font-bold"
            data-testid="invoice-printout-balance"
          >
            {money(inv.balance)}
          </div>
        </div>
      </header>

      <div className="mt-3 h-1 w-24 rounded-full bg-slate-900" />

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <InfoCard title="Billed to">
          <div className="mt-1.5 text-sm font-bold text-slate-900">
            {inv.customer_company || inv.customer_name || "—"}
          </div>
          {inv.customer_company && inv.customer_name && (
            <div className="mt-1.5 text-[13px] text-slate-600">
              Attn: {inv.customer_name}
            </div>
          )}
          {inv.customer_email && (
            <div className="mt-1.5 text-[13px] text-slate-600">
              {inv.customer_email}
            </div>
          )}
          {inv.po_number && (
            <div className="mt-1.5 text-[13px] text-slate-500">
              PO {inv.po_number}
            </div>
          )}
        </InfoCard>

        <InfoCard title="Job address">
          <div className="mt-1.5 whitespace-pre-wrap text-sm font-bold leading-snug text-slate-900">
            {inv.job_address || "—"}
          </div>
          {inv.project_name && (
            <div className="mt-1.5 text-[13px] text-slate-600">
              Project: {inv.project_name}
            </div>
          )}
          {inv.quote_code && (
            <div className="mt-1.5 text-[13px] text-slate-500">
              Quote {inv.quote_code}
            </div>
          )}
        </InfoCard>

        <InfoCard title="Status">
          <div className="mt-1.5 text-sm font-bold text-slate-900">
            {inv.status}
          </div>
          <div className="mt-1.5 flex justify-between gap-2 text-[13px] text-slate-500">
            <span>Invoiced</span>
            <span className="font-mono-data text-slate-700">
              {money(inv.amount)}
            </span>
          </div>
          <div className="mt-1 flex justify-between gap-2 text-[13px] text-slate-500">
            <span>Paid</span>
            <span className="font-mono-data text-slate-700">
              {money(inv.paid)}
            </span>
          </div>
        </InfoCard>
      </div>

      <section className="mt-7 overflow-hidden rounded-xl border border-slate-200">
        <div className="flex flex-wrap items-baseline gap-2 border-b border-slate-200 bg-slate-900 px-4 py-2.5 text-white">
          <span className="text-sm font-bold tracking-tight">{inv.title}</span>
          <span className="ml-auto text-[11px] text-slate-400">
            {inv.kind === "deposit"
              ? "Deposit invoice"
              : inv.kind === "balance"
                ? "Balance invoice"
                : "Invoice"}
          </span>
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-slate-200 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
          <span>Description</span>
          <span className="w-28 text-right">Amount</span>
        </div>
        {lines.map((l, i) => (
          <div
            key={`${l.description}-${i}`}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3 border-b border-slate-100 px-4 py-2.5 text-[13px]"
            data-testid={`invoice-printout-line-${i}`}
          >
            <span className="text-slate-700">{l.description}</span>
            <span className="font-mono-data w-28 text-right tabular-nums text-slate-900">
              {money(l.amount)}
            </span>
          </div>
        ))}
      </section>

      {(inv.payments || []).length > 0 && (
        <section
          className="mt-5 overflow-hidden rounded-xl border border-slate-200"
          data-testid="invoice-printout-payments"
        >
          <div className="border-b border-slate-200 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
            Payments received
          </div>
          {inv.payments.map((p) => (
            <div
              key={p.id}
              className="flex items-baseline justify-between gap-3 border-b border-slate-100 px-4 py-2 text-[13px] last:border-0"
            >
              <span className="text-slate-600">
                {day(p.at)} · {p.method}
                {p.reference ? ` · ${p.reference}` : ""}
              </span>
              <span className="font-mono-data tabular-nums text-slate-900">
                -{money(p.amount)}
              </span>
            </div>
          ))}
        </section>
      )}

      <section className="mt-8 flex justify-end">
        <div className="w-full max-w-sm">
          <div className="flex justify-between gap-3 border-b border-slate-100 py-1.5 text-[13px]">
            <span className="text-slate-500">Invoice total</span>
            <span className="font-mono-data tabular-nums text-slate-800">
              {money(inv.amount)}
            </span>
          </div>
          <div className="flex justify-between gap-3 border-b border-slate-100 py-1.5 text-[13px]">
            <span className="text-slate-500">Paid to date</span>
            <span className="font-mono-data tabular-nums text-slate-800">
              -{money(inv.paid)}
            </span>
          </div>
          <div className="mt-3 flex items-baseline justify-between gap-3 rounded-xl bg-slate-900 px-4 py-3 text-white">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em]">
              Balance due
            </span>
            <span className="font-mono-data text-2xl font-bold tabular-nums">
              {money(inv.balance)}
            </span>
          </div>
        </div>
      </section>

      {inv.notes && (
        <div className="mt-6 whitespace-pre-wrap text-[13px] text-slate-600">
          {inv.notes}
        </div>
      )}
      {(t.has_signature || t.representative) && (
        <div className="mt-8" data-testid="invoice-printout-signature">
          {t.has_signature && (
            <img
              src={`${API}/public/company-signature`}
              alt="Signature"
              className="print-fixed print-fixed-signature h-12 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}
          <div className="mt-1 w-56 border-t border-slate-300 pt-1 text-[12px] text-slate-600">
            {t.representative || t.sales_person || t.company_name}
          </div>
        </div>
      )}
      {inv.terms && (
        <div className="mt-7 whitespace-pre-wrap border-t border-slate-200 pt-4 text-[11px] leading-relaxed text-slate-500">
          {inv.terms}
        </div>
      )}
      {t.footer_text && (
        <div className="mt-4 text-center text-[11px] text-slate-400">
          {t.footer_text}
        </div>
      )}
    </div>
  );
};

export default InvoicePrintout;
